import BaseUI from "../base-ui.ts";
import LogoFragment from "../fragments/logo.ts";
import LobbyFragment from "../fragments/lobby.ts";
import Route from "../../enums/route.ts";
import State from "../../state/state.ts";
import EatOutApiClient from "../../api-client/eat-out-api-client.ts";
import UiUtils from "../../utils/ui-utils.ts";
import LogUtils from "../../utils/log-utils.ts";
import Group from "../../models/group.ts";
import MemberState from "../../enums/member-state.ts";
import V1UserGot from "../../payloads/incoming/v1-user-got.ts";
import V1GroupJoined from "../../payloads/incoming/v1-group-joined.ts";
import SharedApiLogic from "../../api-client/shared-api-logic.ts";
import StateUtils from "../../state/state-utils.ts";
import V1GroupRestaurantsChosen from "../../payloads/incoming/v1-group-restaurants-chosen.ts";
import GroupState from "../../enums/group-state.ts";
import V1GroupFinalResult from "../../payloads/incoming/v1-group-final-result.ts";

export default class TopRestaurantsLobbyScreen extends BaseUI {
  private readonly lobby: LobbyFragment;

  constructor(
    state: State,
    apiClient: EatOutApiClient,
    navigateCallback: (route: Route) => void,
  ) {
    super("top-restaurants-lobby-screen", state, apiClient, navigateCallback);

    // add UI elements
    this.lobby = new LobbyFragment(
      this.state,
      this.apiClient,
      navigateCallback,
    );

    // subscribe to events
    this.apiClient.v1GroupJoinedCallback = this.handleV1GroupJoined.bind(this);
    this.apiClient.v1UserGotCallback = this.handleV1UserGot.bind(this);
    this.apiClient.v1GroupRestaurantsChosenCallback = this.handleV1GroupRestaurantsChosen.bind(this);
    this.apiClient.v1GroupFinalResultCallback = this.handleV1GroupFinalResult.bind(this);

    // fill lobby
    this.fillLobbyWithNotReadyUsers();
  }

  public override render(newElements: HTMLElement[] = []): HTMLDivElement {
    // logo
    const logo: LogoFragment = new LogoFragment(this.state, this.apiClient, this.navigateCallback);
    newElements.push(logo.render());

    // group code
    const groupCode: string = StateUtils.getCurrentGroupCode(this.state);
    const tokenParagraph: HTMLParagraphElement = UiUtils.paragraph(`Code: ${groupCode}`);
    newElements.push(tokenParagraph);

    // "not ready" lobby label
    const isNotReadyParagraph: HTMLParagraphElement = UiUtils.paragraph("Your friends sure are taking a while...");
    newElements.push(isNotReadyParagraph);

    // "not ready" lobby
    newElements.push(this.lobby.render());

    return super.render(newElements);
  }

  private handleV1GroupJoined(v1GroupJoined: V1GroupJoined): void {
    SharedApiLogic.handleV1GroupJoined(this.state, v1GroupJoined);
  }

  private handleV1UserGot(v1UserGot: V1UserGot): void {
    this.lobby.addUser(v1UserGot.payload.user);
  }

  private handleV1GroupRestaurantsChosen(v1GroupRestaurantsChosen: V1GroupRestaurantsChosen): void {
    SharedApiLogic.handleV1GroupRestaurantsChosenCallback(this.debug, this.state, v1GroupRestaurantsChosen);

    this.lobby.removeUser(v1GroupRestaurantsChosen.payload.user_id);
  }

  private handleV1GroupFinalResult(v1GroupFinalResult: V1GroupFinalResult): void {
    // make sure event is related to this client's current group
    if (v1GroupFinalResult.payload.group_id !== this.state.groupState.currentGroupId) {
      LogUtils.error(
        `received V1GroupFinalResult payload for wrong group ID: '${v1GroupFinalResult.payload.group_id}'
          (current group ID: '${this.state.groupState.currentGroupId}')`,
      );
    }

    // save final best restaurant (for lookup later)
    this.state.groupState.updateGroupBestRestaurant(
      v1GroupFinalResult.payload.group_id,
      v1GroupFinalResult.payload.best_restaurant,
    );

    // update group state
    this.state.groupState.updateGroupState(
      v1GroupFinalResult.payload.group_id,
      GroupState.RECEIVED_ALL_RESTAURANT_PREFERENCES,
    );

    // now that we have the final best restaurant, switch to the FinalResult screen
    this.navigateCallback(Route.FINAL_RESULT);
  }

  private fillLobbyWithNotReadyUsers(): void {
    // figure out which users are "Ready" or "Not Ready"
    const group: Group = StateUtils.getCurrentGroup(this.state);
    group.members.forEach((memberState: MemberState, userId: string): void => {
      switch (memberState) {
        case MemberState.SUBMITTED_RESTAURANT_CATEGORY_PREFERENCES: {
          // these users are "Not Ready"
          this.apiClient.sendGetUserPayload(userId);
          return;
        }
        case MemberState.SUBMITTED_RESTAURANT_PREFERENCES: {
          // these users are "Ready"
          return;
        }
        default: {
          LogUtils.error(
            `attempted to render TopChoicesLobby screen, but user '${userId}' has an invalid MemberState: ${memberState}`,
          );
        }
      }
    });
  }
}
